import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Save, X, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { value: 'basic', label: 'Basic Practice' },
  { value: 'comps_ready', label: 'Comps Ready' },
  { value: 'advanced', label: 'Advanced' }
];

// Icon pool for category selection (50 icons)
const ICON_POOL = [
  '📚', '📖', '✏️', '📝', '📋', '📄', '📃', '📑', '🗂️', '📂',
  '🧠', '💡', '🎓', '🎯', '🏆', '⭐', '✨', '💫', '🌟', '🔥',
  '💊', '💉', '🩺', '🔬', '🧪', '🧬', '🦠', '🫀', '🫁', '🦴',
  '🌿', '🌱', '🍃', '🌾', '🌺', '🌸', '🌼', '🌻', '🌷', '🌹',
  '⚕️', '🏥', '🩹', '💊', '🧘', '☯️', '🔮', '🎴', '📿', '🪬',
];

export default function TestCategoriesManager() {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filterTier, setFilterTier] = useState('all');
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    description: '',
    tier: 'basic',
    icon: '📚',
    order: 0,
    enabled: true
  });

  // Function to generate URL-friendly slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-categories`, {
        withCredentials: true
      });
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load test categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/test-categories/${editingCategory.id}`, formData, {
          withCredentials: true
        });
        toast.success('Category updated!');
      } else {
        await axios.post(`${API}/admin/test-categories`, formData, {
          withCredentials: true
        });
        toast.success('Category created!');
      }
      
      setShowForm(false);
      setEditingCategory(null);
      resetForm();
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      slug: category.slug,
      name: category.name,
      description: category.description || '',
      tier: category.tier,
      icon: category.icon || '📚',
      order: category.order,
      enabled: category.enabled
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Delete this category? Tests using this category will need to be reassigned.')) return;
    
    try {
      await axios.delete(`${API}/admin/test-categories/${categoryId}`, {
        withCredentials: true
      });
      toast.success('Category deleted');
      loadCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleToggleEnabled = async (category) => {
    try {
      const updated = { ...category, enabled: !category.enabled };
      await axios.put(`${API}/admin/test-categories/${category.id}`, updated, {
        withCredentials: true
      });
      toast.success(updated.enabled ? 'Category enabled' : 'Category disabled');
      loadCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Seed default test categories? This will create categories for all three tiers.')) return;
    
    try {
      const response = await axios.post(`${API}/admin/test-categories/seed`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed categories');
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      name: '',
      description: '',
      tier: 'basic',
      icon: '📚',
      order: 0,
      enabled: true
    });
  };

  const filteredCategories = filterTier === 'all' 
    ? categories 
    : categories.filter(cat => cat.tier === filterTier);

  const getCategoriesByTier = (tier) => categories.filter(cat => cat.tier === tier);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl retro-title text-[#2C1810]">Test Categories & Sections</h2>
          <p className="text-[#6B4423] mt-1">Manage testing sections for Basic, Comps Ready, and Advanced tiers</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSeed}
            className="retro-button-small bg-[#4D9E7C] flex items-center gap-2"
          >
            Seed Defaults
          </button>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingCategory(null);
              resetForm();
            }}
            className="retro-button-small flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      </div>

      {/* Filter by Tier */}
      <div className="cassette-card p-4">
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-[#2C1810] font-semibold">Filter by Tier:</span>
          <button
            onClick={() => setFilterTier('all')}
            className={`retro-button-small ${filterTier === 'all' ? 'bg-[#E8934D]' : ''}`}
          >
            All ({categories.length})
          </button>
          {TIERS.map(tier => (
            <button
              key={tier.value}
              onClick={() => setFilterTier(tier.value)}
              className={`retro-button-small ${filterTier === tier.value ? 'bg-[#E8934D]' : ''}`}
            >
              {tier.label} ({getCategoriesByTier(tier.value).length})
            </button>
          ))}
        </div>
      </div>

      {/* Categories by Tier */}
      {TIERS.map(tier => {
        const tierCategories = getCategoriesByTier(tier.value);
        if (filterTier !== 'all' && filterTier !== tier.value) return null;
        
        return (
          <div key={tier.value} className="admin-card">
            <h3 className="text-2xl retro-title text-[#F4E4C1] mb-4">{tier.label}</h3>
            
            {tierCategories.length === 0 ? (
              <div className="cassette-card p-8 text-center">
                <p className="text-[#6B4423]">No categories for this tier yet. Add one to get started!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tierCategories.map((category) => (
                  <div key={category.id} className="cassette-card p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">{category.icon}</span>
                          <h4 className="text-xl retro-title text-[#2C1810]">{category.name}</h4>
                          {!category.enabled && (
                            <span className="text-xs px-2 py-1 bg-gray-400 text-white rounded">Hidden</span>
                          )}
                          <span className="text-xs px-2 py-1 bg-[#E8934D] text-white rounded">Order: {category.order}</span>
                        </div>
                        <p className="text-sm text-[#6B4423] mb-2">{category.description}</p>
                        <p className="text-xs text-[#6B4423] font-mono">Slug: {category.slug}</p>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleToggleEnabled(category)}
                          className={`retro-button-small ${category.enabled ? 'bg-[#4D9E7C]' : 'bg-gray-400'}`}
                          title={category.enabled ? 'Hide from students' : 'Show to students'}
                        >
                          {category.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(category)}
                          className="retro-button-small bg-[#E8934D]"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(category.id)}
                          className="retro-button-small bg-[#E85D4D]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="cassette-card max-w-2xl w-full p-8 my-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-[#6B4423] hover:text-[#E85D4D]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Testing Tier *</label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                  disabled={!!editingCategory}
                >
                  {TIERS.map(tier => (
                    <option key={tier.value} value={tier.value}>{tier.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">
                    Slug (URL) {editingCategory ? '' : '- Auto-generated'}
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-gray-100 text-[#2C1810]"
                    placeholder="mens-health"
                    readOnly
                  />
                  <p className="text-xs text-[#6B4423] mt-1">
                    {editingCategory ? 'Slug cannot be changed when editing' : 'Generated from category name'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Icon *</label>
                  <div className="grid grid-cols-10 gap-2 p-3 border-2 border-[#6B4423] rounded bg-[#F4E4C1] max-h-48 overflow-y-auto">
                    {ICON_POOL.map((icon, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={`text-2xl p-2 rounded hover:bg-[#E8934D] transition-colors ${
                          formData.icon === icon ? 'bg-[#E8934D] ring-2 ring-[#6B4423]' : 'bg-white'
                        }`}
                        title={icon}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#6B4423] mt-1">
                    Selected: <span className="text-xl">{formData.icon}</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Category Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    const newSlug = generateSlug(newName);
                    setFormData({ 
                      ...formData, 
                      name: newName, 
                      slug: editingCategory ? formData.slug : newSlug // Don't change slug when editing
                    });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Men's Health"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Description of what this category covers..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Display Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                />
                <p className="text-xs text-[#6B4423] mt-1">Order in which category appears (1, 2, 3...)</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="enabled" className="text-sm font-semibold text-[#6B4423]">
                  Show to students
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="retro-button flex-1 flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingCategory ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="retro-button bg-[#6B4423] hover:bg-[#3D2817] flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
